import { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Input, Segmented, Select, Table, Tabs } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Download, Search } from 'lucide-react';
import Layout from '../../Layout';
import { useKho } from './khoStore';
import {
  ITEM_KIND, WAREHOUSE_KIND, expiryLevel, fmtMoney, fmtNum, fmtQty, itemById, itemStocks, stockLots, whById, type ItemKind, type ItemStock, type Lot,
} from './khoLogic';
import { Badge, Chip, CreateDocButton, ExpiryText, ItemCell, KindTag, PageHeader, exportXlsx, itemUrl, newDocUrl, useKhoPerms } from './khoUi';

type Flag = 'low' | 'expiring' | 'expired' | 'cold' | 'out';

function StatusBadges({ r, now }: { r: ItemStock; now: number }) {
  return (
    <div className="flex flex-wrap gap-1">
      {r.qty <= 0 && <Badge className="bg-gray-100 text-gray-500 border-gray-200">Hết hàng</Badge>}
      {r.low && r.qty > 0 && <Badge className="bg-orange-50 text-orange-700 border-orange-200">Sắp hết</Badge>}
      {r.expiry === 'expired' && <Badge className="bg-rose-50 text-rose-700 border-rose-200">Có lô hết hạn</Badge>}
      {(r.expiry === 'urgent' || r.expiry === 'near') && <Badge className="bg-amber-50 text-amber-700 border-amber-200">Cận hạn</Badge>}
      {r.qty > 0 && !r.low && r.expiry !== 'expired' && r.expiry !== 'urgent' && r.expiry !== 'near' && <span className="text-xs text-emerald-600">Bình thường</span>}
    </div>
  );
}

/** Tồn kho theo mặt hàng / theo lô, lọc theo kho và cảnh báo */
export default function StockPage() {
  const s = useKho();
  const navigate = useNavigate();
  const { canCreate } = useKhoPerms();
  const now = Date.now();
  const [params, setParams] = useSearchParams();
  const wh = params.get('wh') || '';
  const view = params.get('view') === 'lot' ? 'lot' : 'item';
  const kind = (params.get('kind') as ItemKind) || null;
  const flag = (params.get('flag') as Flag) || null;
  const q = params.get('q') || '';
  const go = (p: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    Object.entries(p).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
    setParams(next, { replace: true });
  };

  const textMatch = (name: string, code: string, lot = '') => {
    const k = q.trim().toLowerCase();
    return !k || name.toLowerCase().includes(k) || code.toLowerCase().includes(k) || lot.toLowerCase().includes(k);
  };

  const stocks = useMemo(() => itemStocks(s, now, wh || undefined), [s, wh]); // eslint-disable-line react-hooks/exhaustive-deps
  const itemRows = stocks.filter(r => {
    if (!r.item.active && r.qty <= 0) return false;
    if (wh && r.qty <= 0 && flag !== 'low') return false;
    if (kind && r.item.kind !== kind) return false;
    if (flag === 'low' && !r.low) return false;
    if (flag === 'expiring' && r.expiry !== 'urgent' && r.expiry !== 'near') return false;
    if (flag === 'expired' && r.expiry !== 'expired') return false;
    if (flag === 'cold' && !r.item.cold) return false;
    if (flag === 'out' && r.qty > 0) return false;
    return textMatch(r.item.name, r.item.code);
  });

  const lotRows = stockLots(s).filter(l => {
    const item = itemById(s, l.itemId);
    if (!item) return false;
    if (wh && l.warehouseId !== wh) return false;
    if (kind && item.kind !== kind) return false;
    const lv = expiryLevel(l.expiry, now);
    if (flag === 'expiring' && lv !== 'urgent' && lv !== 'near') return false;
    if (flag === 'expired' && lv !== 'expired') return false;
    if (flag === 'cold' && !item.cold) return false;
    if (flag === 'low' && !stocks.find(r => r.item.id === l.itemId)?.low) return false;
    if (flag === 'out') return false;
    return textMatch(item.name, item.code, l.lotNo);
  }).sort((a, b) => (a.expiry || '9999').localeCompare(b.expiry || '9999'));

  const totalValue = view === 'item' ? itemRows.reduce((t, r) => t + r.value, 0) : lotRows.reduce((t, l) => t + l.qty * l.unitCost, 0);
  const counts = {
    low: stocks.filter(r => r.low).length,
    expiring: stocks.filter(r => r.expiry === 'urgent' || r.expiry === 'near').length,
    expired: stocks.filter(r => r.expiry === 'expired').length,
  };

  const itemColumns: ColumnsType<ItemStock> = [
    { title: 'Mặt hàng', key: 'name', render: (_, r) => <ItemCell item={r.item} sub={r.item.brand} /> },
    { title: 'Loại', key: 'kind', width: 160, render: (_, r) => <KindTag item={r.item} /> },
    { title: 'Tồn kho', key: 'qty', align: 'right', sorter: (a, b) => a.qty - b.qty, render: (_, r) => <span className={r.qty <= 0 ? 'text-gray-400' : 'font-semibold'}>{fmtQty(r.item, r.qty)}</span> },
    { title: 'Tối thiểu', key: 'min', align: 'right', width: 110, render: (_, r) => (r.item.minStock ? <span className="text-gray-500">{fmtNum(r.item.minStock)} {r.item.unit}</span> : <span className="text-gray-300">—</span>) },
    { title: 'Giá trị tồn', key: 'value', align: 'right', width: 140, sorter: (a, b) => a.value - b.value, render: (_, r) => fmtMoney(r.value) },
    { title: 'HSD gần nhất', key: 'exp', width: 190, render: (_, r) => <ExpiryText expiry={r.nearestExpiry} now={now} short /> },
    { title: 'Trạng thái', key: 'st', width: 170, render: (_, r) => <StatusBadges r={r} now={now} /> },
  ];

  const lotColumns: ColumnsType<Lot> = [
    { title: 'Mặt hàng', key: 'name', render: (_, l) => <ItemCell item={itemById(s, l.itemId)} /> },
    { title: 'Số lô', dataIndex: 'lotNo', key: 'lot', width: 120, render: v => <span className="font-mono text-xs">{v}</span> },
    { title: 'Hạn sử dụng', key: 'exp', width: 210, render: (_, l) => <ExpiryText expiry={l.expiry} now={now} /> },
    { title: 'Kho', key: 'wh', width: 200, render: (_, l) => whById(s, l.warehouseId)?.name },
    { title: 'Số lượng', key: 'qty', align: 'right', render: (_, l) => <span className="font-semibold">{fmtQty(itemById(s, l.itemId), l.qty)}</span> },
    { title: 'Giá trị', key: 'value', align: 'right', width: 140, render: (_, l) => fmtMoney(l.qty * l.unitCost) },
    {
      title: '', key: 'act', width: 110, render: (_, l) => {
        if (!canCreate) return null;
        const lv = expiryLevel(l.expiry, now);
        const extra = `wh=${l.warehouseId}&item=${l.itemId}&lot=${encodeURIComponent(l.lotNo)}`;
        if (lv === 'expired') return <Button size="small" danger onClick={e => { e.stopPropagation(); navigate(newDocUrl('dispose', `${extra}&reason=${encodeURIComponent('Hết hạn sử dụng')}`)); }}>Xuất hủy</Button>;
        return null;
      },
    },
  ];

  const doExport = () => {
    if (view === 'item') {
      exportXlsx('ton_kho', 'Tồn kho', itemRows.map(r => ({
        'Mã': r.item.code, 'Tên': r.item.name, 'Loại': ITEM_KIND[r.item.kind].label, 'Đơn vị': r.item.unit, 'Tồn': r.qty, 'Tối thiểu': r.item.minStock,
        'Giá trị': Math.round(r.value), 'HSD gần nhất': r.nearestExpiry || '', 'Sắp hết': r.low ? 'x' : '',
      })));
    } else {
      exportXlsx('ton_kho_theo_lo', 'Theo lô', lotRows.map(l => {
        const item = itemById(s, l.itemId)!;
        return { 'Mã': item.code, 'Tên': item.name, 'Lô': l.lotNo, 'HSD': l.expiry || '', 'Kho': whById(s, l.warehouseId)?.name || '', 'Số lượng': l.qty, 'Đơn vị': item.unit, 'Giá trị': Math.round(l.qty * l.unitCost) };
      }));
    }
  };

  const whCount = (id: string) => new Set(stockLots(s).filter(l => l.warehouseId === id).map(l => l.itemId)).size;

  return (
    <Layout>
      <div className="flex-1 flex flex-col min-w-0 gap-4">
        <PageHeader title="Tồn kho" desc="Tồn theo mặt hàng và theo lô — hàng cận hạn được xếp lên đầu để ưu tiên dùng trước (FEFO)"
          extra={<><Button icon={<Download size={14} />} onClick={doExport}>Xuất Excel</Button><CreateDocButton /></>} />

        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm">
          <div className="px-4">
            <Tabs activeKey={wh || 'all'} onChange={k => go({ wh: k === 'all' ? null : k })} items={[
              { key: 'all', label: `Tất cả kho (${new Set(stockLots(s).map(l => l.itemId)).size})` },
              ...s.warehouses.map(w => ({ key: w.id, label: <span title={`${WAREHOUSE_KIND[w.kind]} · thủ kho ${w.keeper}`}>{w.name} ({whCount(w.id)})</span> })),
            ]} />
          </div>

          <div className="px-4 pb-3 flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <Segmented value={view} onChange={v => go({ view: v === 'lot' ? 'lot' : null })} options={[{ value: 'item', label: 'Theo mặt hàng' }, { value: 'lot', label: 'Theo lô' }]} />
              <Input allowClear className="!w-full sm:!w-64" prefix={<Search size={14} className="text-gray-400" />} placeholder="Tìm tên, mã hàng, số lô"
                value={q} onChange={e => go({ q: e.target.value || null })} />
              <Select allowClear className="w-full sm:w-[190px]" placeholder="Loại hàng" value={kind || undefined} onChange={v => go({ kind: v || null })}
                options={(Object.keys(ITEM_KIND) as ItemKind[]).map(k => ({ value: k, label: ITEM_KIND[k].label }))} />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Chip active={!flag} onClick={() => go({ flag: null })}>Tất cả</Chip>
              <Chip tone="amber" active={flag === 'low'} onClick={() => go({ flag: flag === 'low' ? null : 'low' })}>Sắp hết ({counts.low})</Chip>
              <Chip tone="amber" active={flag === 'expiring'} onClick={() => go({ flag: flag === 'expiring' ? null : 'expiring' })}>Cận hạn ≤ 60 ngày ({counts.expiring})</Chip>
              <Chip tone="rose" active={flag === 'expired'} onClick={() => go({ flag: flag === 'expired' ? null : 'expired' })}>Có lô hết hạn ({counts.expired})</Chip>
              <Chip tone="sky" active={flag === 'cold'} onClick={() => go({ flag: flag === 'cold' ? null : 'cold' })}>Bảo quản lạnh</Chip>
              {view === 'item' && <Chip active={flag === 'out'} onClick={() => go({ flag: flag === 'out' ? null : 'out' })}>Hết hàng</Chip>}
              <span className="ml-auto text-xs text-gray-500">
                {view === 'item' ? `${itemRows.length} mặt hàng` : `${lotRows.length} lô`} · giá trị <b className="text-gray-800 dark:text-gray-200">{fmtMoney(totalValue)}</b>
              </span>
            </div>
          </div>

          {view === 'item' ? (
            <Table<ItemStock> rowKey={r => r.item.id} size="middle" columns={itemColumns} dataSource={itemRows} scroll={{ x: 1000 }}
              pagination={{ pageSize: 20, hideOnSinglePage: true }}
              rowClassName={r => `cursor-pointer ${r.expiry === 'expired' ? 'bg-rose-50/40' : ''}`}
              onRow={r => ({ onClick: () => navigate(itemUrl(r.item.id, wh ? `&wh=${wh}` : '')) })} />
          ) : (
            <Table<Lot> rowKey="key" size="middle" columns={lotColumns} dataSource={lotRows} scroll={{ x: 1000 }}
              pagination={{ pageSize: 20, hideOnSinglePage: true }}
              rowClassName={l => `cursor-pointer ${expiryLevel(l.expiry, now) === 'expired' ? 'bg-rose-50/40' : ''}`}
              onRow={l => ({ onClick: () => navigate(itemUrl(l.itemId, `&wh=${l.warehouseId}`)) })} />
          )}
        </div>
      </div>
    </Layout>
  );
}
